import { useState } from "react";
import Button from "ui/buttons/Button";
import { Popover } from "./Popover";

## Basic Usage

<Popover.Root>
  <Popover.Trigger>
    <Button btnStyle="secondary">Open Popover</Button>
  </Popover.Trigger>
  <Popover.Content>
    <div style={{ padding: "0.5rem" }}>
      <p>This is popover content!</p>
    </div>
  </Popover.Content>
</Popover.Root>

---

## Positions

<div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
  <Popover.Root>
    <Popover.Trigger>
      <Button btnStyle="secondary">Top</Button>
    </Popover.Trigger>
    <Popover.Content side="top">
      <div style={{ padding: "0.5rem" }}>Top popover</div>
    </Popover.Content>
  </Popover.Root>
  <Popover.Root>
    <Popover.Trigger>
      <Button btnStyle="secondary">Right</Button>
    </Popover.Trigger>
    <Popover.Content side="right">
      <div style={{ padding: "0.5rem" }}>Right popover</div>
    </Popover.Content>
  </Popover.Root>
  <Popover.Root>
    <Popover.Trigger>
      <Button btnStyle="secondary">Bottom</Button>
    </Popover.Trigger>
    <Popover.Content side="bottom">
      <div style={{ padding: "0.5rem" }}>Bottom popover</div>
    </Popover.Content>
  </Popover.Root>
  <Popover.Root>
    <Popover.Trigger>
      <Button btnStyle="secondary">Left</Button>
    </Popover.Trigger>
    <Popover.Content side="left">
      <div style={{ padding: "0.5rem" }}>Left popover</div>
    </Popover.Content>
  </Popover.Root>
</div>

---

## Alignment

<div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
  <Popover.Root>
    <Popover.Trigger>
      <Button btnStyle="secondary">Start</Button>
    </Popover.Trigger>
    <Popover.Content side="bottom" align="start">
      <div style={{ padding: "0.5rem" }}>Aligned to start</div>
    </Popover.Content>
  </Popover.Root>
  <Popover.Root>
    <Popover.Trigger>
      <Button btnStyle="secondary">Center</Button>
    </Popover.Trigger>
    <Popover.Content side="bottom" align="center">
      <div style={{ padding: "0.5rem" }}>Aligned to center</div>
    </Popover.Content>
  </Popover.Root>
  <Popover.Root>
    <Popover.Trigger>
      <Button btnStyle="secondary">End</Button>
    </Popover.Trigger>
    <Popover.Content side="bottom" align="end">
      <div style={{ padding: "0.5rem" }}>Aligned to end</div>
    </Popover.Content>
  </Popover.Root>
</div>

---

## With Close Button

<Popover.Root>
  <Popover.Trigger>
    <Button btnStyle="secondary">Open with close</Button>
  </Popover.Trigger>
  <Popover.Content style={{ minWidth: "200px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem" }}>
      <span style={{ fontWeight: 500 }}>Title</span>
      <Popover.Close />
    </div>
    <div style={{ padding: "0 0.5rem 0.5rem" }}>
      Content with a close button in the header.
    </div>
  </Popover.Content>
</Popover.Root>

---

## With Arrow

<Popover.Root>
  <Popover.Trigger>
    <Button btnStyle="secondary">With Arrow</Button>
  </Popover.Trigger>
  <Popover.Content>
    <Popover.Arrow />
    <div style={{ padding: "0.5rem" }}>Popover with an arrow pointer</div>
  </Popover.Content>
</Popover.Root>

---

## Controlled State

export function ControlledPopover() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger>
          <Button btnStyle="secondary">Controlled</Button>
        </Popover.Trigger>
        <Popover.Content>
          <div style={{ padding: "0.5rem" }}>
            <p>Controlled popover</p>
            <Button btnStyle="tertiary" btnSize="small" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        </Popover.Content>
      </Popover.Root>
      <span>State: {open ? "open" : "closed"}</span>
    </div>
  );
}

<ControlledPopover />

---

## Rich Content

<Popover.Root>
  <Popover.Trigger>
    <Button btnStyle="secondary">User Menu</Button>
  </Popover.Trigger>
  <Popover.Content style={{ minWidth: "200px" }}>
    <div style={{ padding: "0.75rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
        <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#e5e5e5" }} />
        <div>
          <div style={{ fontWeight: 500 }}>John Doe</div>
          <div style={{ fontSize: "12px", color: "#666" }}>john@example.com</div>
        </div>
      </div>
      <hr style={{ border: "none", borderTop: "1px solid #e5e5e5", margin: "0.5rem 0" }} />
      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        <div style={{ padding: "0.25rem 0", cursor: "pointer" }}>Profile</div>
        <div style={{ padding: "0.25rem 0", cursor: "pointer" }}>Settings</div>
        <div style={{ padding: "0.25rem 0", cursor: "pointer", color: "#dc2626" }}>Sign out</div>
      </div>
    </div>
  </Popover.Content>
</Popover.Root>

---

## Form in Popover

export function FormPopover() {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Button btnStyle="secondary">Add Note</Button>
      </Popover.Trigger>
      <Popover.Content style={{ width: "280px" }}>
        <div style={{ padding: "0.75rem" }}>
          <div style={{ marginBottom: "0.5rem", fontWeight: 500 }}>Add a note</div>
          <textarea
            placeholder="Enter your note..."
            style={{ width: "100%", minHeight: "80px", padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc", resize: "vertical" }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
            <Button btnStyle="tertiary" btnSize="small" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button btnStyle="primary" btnSize="small" onClick={() => setOpen(false)}>
              Save
            </Button>
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

<FormPopover />


